import { useMutation } from '@apollo/client';
import { LOG_UPLOAD_FAILURE, UPLOAD_PARAMS } from '..';
import { useUploadContext } from '../Providers';
const PROBE_TIMEOUT_MS = 5000;
// S3 echoes form fields such as the security token in its error body, so keep only the error
// code and message (or the title of a proxy's block page) and mask every value we posted.
const summarizeResponse = (text, fields) => {
    const match = (pattern) => text.match(pattern)?.[1];
    const summary = [match(/<Code>([^<]*)<\/Code>/), match(/<Message>([^<]*)<\/Message>/)]
        .filter(Boolean)
        .join(': ') ||
        match(/<title[^>]*>([^<]*)<\/title>/i) ||
        (text ? `unrecognized ${text.length}-character response` : '');
    return Object.entries(fields).reduce((result, [name, value]) => (value ? result.split(value).join(`<${name}>`) : result), summary);
};
// A no-cors GET can't read the response, but it only fails when the host can't be reached at all.
const isReachable = (url) => Promise.race([
    fetch(url, { mode: 'no-cors', cache: 'no-store' }).then(() => true, () => false),
    new Promise(resolve => setTimeout(() => resolve(false), PROBE_TIMEOUT_MS)),
]);
// Resolves relative URLs the way XHR does, and returns undefined instead of throwing on a bad one.
const hostOf = (url) => {
    try {
        return url ? new URL(url, window.location.href).host : undefined;
    }
    catch {
        return undefined;
    }
};
export const useUpload = ({ s3KeyPrefix, onUploaded }) => {
    const [getUploadParams] = useMutation(UPLOAD_PARAMS);
    const [logUploadFailure] = useMutation(LOG_UPLOAD_FAILURE);
    const { uploading, setUploading } = useUploadContext();
    // The browser posts the file straight to S3, so this log is the only way the backend
    // hears about a failed upload. It never throws: the member already sees the upload error.
    const logFailure = async ({ stage, error, file, uploadParams, xhr, elapsedMs, }) => {
        try {
            // uploadParams is parsed JSON, so read optional values defensively.
            const s3Key = uploadParams?.fields?.key;
            const isS3Post = stage === 's3_post' && !!uploadParams;
            await logUploadFailure({
                variables: {
                    input: {
                        stage,
                        errorMessage: error instanceof Error ? error.message : String(error),
                        httpStatus: isS3Post ? xhr.status : error?.networkError?.statusCode,
                        responseSummary: isS3Post
                            ? summarizeResponse(xhr.responseText, uploadParams.fields)
                            : undefined,
                        elapsedMs,
                        s3Reachable: isS3Post && xhr.status === 0 ? await isReachable(uploadParams.url) : undefined,
                        uploadHost: hostOf(uploadParams?.url),
                        uploadId: s3Key?.split('/').slice(-2, -1)[0],
                        keyPrefix: s3KeyPrefix,
                        contentType: file.type,
                        fileExtension: file.name.includes('.')
                            ? file.name.split('.').pop()?.toLowerCase()
                            : undefined,
                        fileSizeBytes: file.size,
                        online: navigator.onLine,
                    },
                },
            });
        }
        catch { } // swallow logging errors
    };
    const handleUpload = async ({ file, onError, onSuccess, onProgress }) => {
        const xhr = new XMLHttpRequest();
        let stage = 'upload_params';
        let stageStartedAt = Date.now();
        let uploadParams;
        try {
            const fileType = file.type;
            setUploading(true);
            const result = await getUploadParams({
                variables: {
                    input: { fileName: file.name, keyPrefix: s3KeyPrefix, contentType: fileType },
                },
            });
            uploadParams = JSON.parse(result.data.uploadParams);
            const formData = new FormData();
            Object.keys(uploadParams.fields).forEach(attr => {
                formData.append(attr, uploadParams.fields[attr]);
            });
            formData.append('file', file);
            stage = 's3_post';
            stageStartedAt = Date.now();
            const uploadResponse = await new Promise((resolve, reject) => {
                if (onProgress) {
                    xhr.upload.addEventListener('progress', event => {
                        if (event.lengthComputable) {
                            onProgress({
                                percent: Math.round((event.loaded / event.total) * 100),
                            }, file);
                        }
                    });
                }
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve({
                            status: xhr.status,
                            statusText: xhr.statusText,
                            data: xhr.response,
                        });
                    }
                    else {
                        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
                    }
                };
                xhr.onerror = () => reject(new Error('Network error'));
                xhr.onabort = () => reject(new Error('Upload aborted'));
                xhr.open('POST', uploadParams.url);
                xhr.send(formData);
            });
            stage = 'on_uploaded';
            stageStartedAt = Date.now();
            onUploaded(uploadParams.fields.key, file);
            if (onSuccess) {
                onSuccess(uploadResponse, file);
            }
        }
        catch (e) {
            logFailure({
                stage,
                error: e,
                file,
                uploadParams,
                xhr,
                elapsedMs: Date.now() - stageStartedAt,
            });
            if (onError) {
                onError(e);
            }
        }
        finally {
            setUploading(false);
        }
    };
    return { handleUpload, uploading };
};
